/**
 * Turning a grid of typed strings into a `CreateBearRally`.
 *
 * Kept as a pure function, separate from the form, because one rule here
 * decides whether the whole Bear analysis is trustworthy: **a blank cell is
 * absent, not zero**.
 *
 * The operator recording a session frequently cannot see a joiner's troop
 * split. If a blank became `0`, that joiner would appear to have brought no
 * troops, and every damage-per-troop figure computed downstream would be wrong
 * in a way nothing later could detect — an absent value announces itself, a
 * fabricated zero does not (spec section 56, and AGENTS.md "Unknown zero is not
 * zero").
 *
 * Absent fields are therefore omitted from the payload entirely rather than
 * sent as null, so the shape says "not recorded" rather than "recorded as
 * nothing".
 */

import type { CreateBearRally } from '@kso/schemas';
import { parseOptionalInt } from '../../lib/format';
import { tr } from '../../i18n';

export type RallyHeaderInput = {
  leaderPlayerId: string;
  wave: string;
  sequence: string;
  rallyCapacity: string;
  actualTroops: string;
  totalDamage: string;
  leaderHeroIds: string;
};

export type RallyMemberInput = {
  playerId: string;
  joinOrder: string;
  captainHeroId: string;
  infantry: string;
  cavalry: string;
  archer: string;
  totalTroops: string;
  damage: string;
};

export type BuildResult =
  | { ok: true; payload: CreateBearRally }
  | { ok: false; message: string };

export function buildRallyPayload(
  header: RallyHeaderInput,
  rows: readonly RallyMemberInput[],
): BuildResult {
  // A pure function, so it reads the active locale rather than taking a
  // dictionary parameter its two callers would have to thread through.
  const t = tr().buildRally;

  if (!header.leaderPlayerId) {
    return { ok: false, message: t.needsLead };
  }

  const invalid: string[] = [];

  const optionalInt = (raw: string, label: string): number | null => {
    const parsed = parseOptionalInt(raw);
    if (parsed === 'INVALID') {
      invalid.push(label);
      return null;
    }
    return parsed;
  };

  const wave = optionalInt(header.wave, t.fields.wave);
  const sequence = optionalInt(header.sequence, t.fields.order);
  const rallyCapacity = optionalInt(header.rallyCapacity, t.fields.rallyCapacity);
  const actualTroops = optionalInt(header.actualTroops, t.fields.actualTroops);
  const totalDamage = optionalInt(header.totalDamage, t.fields.totalRallyDamage);

  const heroIds = header.leaderHeroIds
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number.parseInt(part, 10) : Number.NaN));
  if (heroIds.some((id) => !Number.isFinite(id))) invalid.push(t.fields.leadHeroIds);
  if (heroIds.length > 3) invalid.push(t.fields.leadHeroIdsMax);

  const filled = rows.filter((row) => row.playerId.trim() !== '');

  const members = filled.map((row, index) => {
    const rowNumber = index + 1;
    const joinOrder = optionalInt(row.joinOrder, t.rowField(rowNumber, t.fields.joinOrder));
    const captainHeroId = optionalInt(row.captainHeroId, t.rowField(rowNumber, t.fields.captainHero));
    const infantry = optionalInt(row.infantry, t.rowField(rowNumber, t.fields.infantry));
    const cavalry = optionalInt(row.cavalry, t.rowField(rowNumber, t.fields.cavalry));
    const archer = optionalInt(row.archer, t.rowField(rowNumber, t.fields.archer));
    const totalTroops = optionalInt(row.totalTroops, t.rowField(rowNumber, t.fields.totalTroops));
    const damage = optionalInt(row.damage, t.rowField(rowNumber, t.fields.damage));

    // A troops object appears only when at least one component was entered.
    // An object of three nulls means the same thing as no object, but it reads
    // to a later maintainer as "the split was recorded", which it was not.
    const troops =
      infantry === null && cavalry === null && archer === null
        ? undefined
        : {
            ...(infantry !== null ? { infantry } : {}),
            ...(cavalry !== null ? { cavalry } : {}),
            ...(archer !== null ? { archer } : {}),
          };

    return {
      playerId: row.playerId,
      ...(joinOrder !== null ? { joinOrder } : {}),
      ...(captainHeroId !== null ? { captainHeroId } : {}),
      ...(troops ? { troops } : {}),
      ...(totalTroops !== null ? { totalTroops } : {}),
      ...(damage !== null ? { damage } : {}),
    };
  });

  if (invalid.length > 0) {
    // The message has to say that blank is allowed, or an operator who cannot
    // see a joiner's troop split will type a 0 to make the form stop
    // complaining, and that 0 is indistinguishable from a measurement.
    return { ok: false, message: t.invalidFields(invalid.join(', ')) };
  }

  const duplicates = [
    ...new Set(
      members
        .map((member) => member.playerId)
        .filter((playerId, index, all) => all.indexOf(playerId) !== index),
    ),
  ];
  if (duplicates.length > 0) {
    return { ok: false, message: t.duplicatePlayers(duplicates.join(', ')) };
  }

  return {
    ok: true,
    payload: {
      leaderPlayerId: header.leaderPlayerId,
      ...(wave !== null ? { wave } : {}),
      ...(sequence !== null ? { sequence } : {}),
      ...(rallyCapacity !== null ? { rallyCapacity } : {}),
      ...(actualTroops !== null ? { actualTroops } : {}),
      ...(totalDamage !== null ? { totalDamage } : {}),
      ...(heroIds.length > 0 ? { leaderHeroIds: heroIds } : {}),
      ...(members.length > 0 ? { members } : {}),
    },
  };
}
